import { CHART_COLORS, CHART_FONTS, CHART_STYLES, formatNumberWithSuffix }
  from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';
import { FairValueAndPpChartsService, FairValueByCompanyBarChartsData, FairValuePieChartsData, MIL }
  from '../calculations-service/fair-value-and-pp-charts.service';
import { FmvChartOptionsTypes, FmvChartsFinData, FmvChartsInformation }
  from '../../pojo/fmv-charts-classes';
import { PpChartsMenuValues } from '../../pojo/fmv-charts-enums';
import { PortfoliosPerformanceBarChartData } from '../../../../../chart/ChartAuxiliary';

export class FairValueAndPpChartsOptionsService {

  /**
   * Fill data for pie and bar charts based on the given financial data type (FMV or Investment).
   *
   * @param chartsData Financial data for either FMV or Investment.
   * @param parameter Indicates whether we are dealing with FMV or Investment.
   * @param multiplicator used in investor positioning to calculate investor's data based on his commitment in Fund
   * @returns Prepared options for charts.
   */
  static fillChartOptionsForType(chartsData: FmvChartsFinData, parameter: PpChartsMenuValues, multiplicator = 1): FmvChartOptionsTypes {
    const chartOptions = new FmvChartOptionsTypes();
    // Handle pie charts
    for (const key in pieChartMap) {
      if (chartsData.pieChart.hasOwnProperty(key)) {
        chartOptions.pieChart[key] = FairValueAndPpChartsOptionsService
          .createPieChartOptions(pieChartMap[key], chartsData.pieChart[key] || [], parameter, multiplicator);
      }
    }

    // Handle bar charts
    for (const key in barChartMap) {
      if (chartsData.barChart.hasOwnProperty(key) && key !== 'byCompany') {
        chartOptions.barChart[key] = FairValueAndPpChartsOptionsService
          .createDrillDownBarChartOptions(barChartMap[key], chartsData.barChart[key], parameter, multiplicator);
      }
    }
    // Handle company bar chart separately
    if (chartsData.barChart.byCompany) {
      chartOptions.barChart.byCompany = FairValueAndPpChartsOptionsService
        .createBarChartByCompanyOptions(chartsData.barChart.byCompany || [], parameter, multiplicator);
    }

    return chartOptions;
  }

  /**
   * Fill data for fair-value and investment pie charts.
   * The additional, unique options will be set to complete data in base-pie-chart.
   *
   * @param chartId id of chart for html render
   * @param chartData financial chart data
   * @param parameter show, if pie chart used for fair value or for investment data
   * @param multiplicator used in investor positioning to calculate investor's data based on his commitment in Fund
   * @returns chart id and additional options for base pie chart
   */
  private static createPieChartOptions(
    chartId: string,
    chartData: FairValuePieChartsData[],
    parameter: PpChartsMenuValues,
    multiplicator = 1
  ): FmvChartsInformation {
    const filteredData = chartData
      .filter((item) => item.y !== 0)
      .map((item) => ({ ...item, y: multiplicator * item.y / MIL }));

    const total = formatNumberWithSuffix(chartData.reduce((sum, p) => sum + p.y, 0), 2);

    return {
      chartId,
      additionalOptions: {
        tooltip: CHART_STYLES.tooltipOneLineWithPcnt,
        series: [{
          name: parameter === PpChartsMenuValues.fmv ? 'Fair market value' : 'Investment',
          data: filteredData,
        }],
        title: {
          text: total,
          align: 'center',
          verticalAlign: 'middle',
        },
      },
    };
  }


  /**
   * Fill data for fair-value and investment base bar charts.
   * The additional, unique options will be set to complete data in base-bar-chart.
   *
   * IMPORTANT NOTE:
   * to add xAxis categories (or something else), you should add the whole xAxis object,
   * otherwise the chart will consider only categories, without all other styles, other styles will be removed
   *
   * WRONG:
   * xAxis: [{
   *       categories: sortedData.map((item: any) => item.name)
   * }]
   *
   * CORRECT:
   * xAxis: [{
   *       categories: sortedData.map((item: any) => item.name)
   *       type: 'category',
   *       crosshair: true,
   *       ... bla bla bla
   * }]
   *
   * @param chartId id of chart for html render
   * @param chartData financial chart data
   * @param parameter show, if chart used for fair value or for investment data
   * @param multiplicator used in investor positioning to calculate investor's data based on his commitment in Fund
   * @returns chart id and additional options for base bar chart
   */
  private static createBarChartByCompanyOptions(
    chartData: FairValueByCompanyBarChartsData[],
    parameter: PpChartsMenuValues,
    multiplicator = 1
  ): FmvChartsInformation {
    const investmentData = chartData.map((item: FairValueByCompanyBarChartsData) => multiplicator * item.cost / MIL);
    const gainData = chartData.map((item: FairValueByCompanyBarChartsData) => multiplicator * item.gain / MIL);
    const moicData = chartData.map((item: FairValueByCompanyBarChartsData) => (item.cost + item.gain) / item.cost);

    const series = [
      {
        name: 'Investment',
        type: 'column',
        color: CHART_COLORS.grey,
        yAxis: 0,
        data: investmentData,
        showInLegend: parameter === PpChartsMenuValues.fmv,
        visible: true,
        lineWidth: 1,
        zIndex: 1,
      },
    ];

    if (parameter === PpChartsMenuValues.fmv) {
      series.unshift({
        name: 'Gain/(loss)',
        type: 'column',
        color: CHART_COLORS.green,
        yAxis: 0,
        data: gainData,
        showInLegend: true,
        visible: true,
        lineWidth: 1,
        zIndex: 1,
      });
      series.unshift({
        name: 'MOIC',
        type: 'spline',
        color: CHART_COLORS.orange,
        yAxis: 1,
        data: moicData,
        showInLegend: true,
        visible: false,
        lineWidth: 0,
        zIndex: 10,
      });
    }

    return {
      chartId: 'bar-chart-fair-value-by-company',
      additionalOptions: {
        tooltip: parameter === PpChartsMenuValues.fmv ?
          CHART_STYLES.customFairValueTooltipWithLine :
          CHART_STYLES.tooltipTwoLinesWithUnitFormatter,
        series,
        yAxis: [{
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithM,
          stackLabels: CHART_STYLES.grandTotalStackLabels(),
        }, {
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.xAxisLabelsStyle,
          opposite: true,
        }],
        xAxis: [{
          categories: chartData.map((item) => item.companyData.name),
          type: 'category',
          crosshair: true,
          style: { color: '#333333', fontSize: CHART_FONTS.font4 },
          labels: {
            rotation: -45,
            style: {
              textOverflow: 'ellipsis',
              width: 75,
              fontSize: CHART_FONTS.font4,
            },
          },
          scrollbar: CHART_STYLES.scrollbar,
        }],
      },
    };
  }


  /**
   * Fill data for fair-value and investment base bar drilldown charts.
   * The additional, unique options will be set to complete data in base-bar-drilldown-chart.
   *
   * IMPORTANT NOTE:
   * to add xAxis categories (or something else), you should add the whole xAxis object,
   * otherwise the chart will consider only categories, without all other styles, other styles will be removed
   *
   * @param chartId id of chart for html render
   * @param chartData financial chart data
   * @param parameter show, if chart used for fair value or for investment data
   * @param multiplicator used in investor positioning to calculate investor's data based on his commitment in Fund
   * @returns chart id and additional options for base bar chart
   */
  private static createDrillDownBarChartOptions(
    chartId: string,
    chartData: PortfoliosPerformanceBarChartData,
    parameter: PpChartsMenuValues,
    multiplicator = 1
  ): FmvChartsInformation {
    if (!chartData) {
      return null;
    }

    const investmentData = chartData.mainData.map((item) => ({
      y: multiplicator * item.investment.y / MIL,
      drilldown: item.investment.drilldown,
    }));

    const gainData = chartData.mainData.map((item) => ({
      y: multiplicator * item.gain.y / MIL,
      drilldown: item.gain.drilldown,
    }));

    const moicData = chartData.mainData.map((item) => ({
      y: item.moic.y,
      drilldown: item.moic.drilldown,
    }));

    const series = [
      {
        name: 'Investment',
        type: 'column',
        color: CHART_COLORS.grey,
        yAxis: 0,
        data: investmentData,
        showInLegend: parameter === PpChartsMenuValues.fmv,
        visible: true,
        lineWidth: 0,
        zIndex: 1,
      },
    ];

    if (parameter === PpChartsMenuValues.fmv) {
      series.unshift({
        name: 'Gain/(loss)',
        type: 'column',
        color: CHART_COLORS.green,
        yAxis: 0,
        data: gainData,
        showInLegend: true,
        visible: true,
        lineWidth: 0,
        zIndex: 1,
      });
      series.unshift({
        name: 'MOIC',
        type: 'spline',
        color: CHART_COLORS.orange,
        yAxis: 1,
        data: moicData,
        showInLegend: true,
        visible: false,
        lineWidth: 0,
        zIndex: 10,
      });
    }

    return {
      chartId,
      additionalOptions: {
        tooltip: parameter === PpChartsMenuValues.fmv ?
          CHART_STYLES.customFairValueTooltipWithLine :
          CHART_STYLES.tooltipTwoLinesWithUnitFormatter,
        series,
        yAxis: [{
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithM,
          stackLabels: CHART_STYLES.grandTotalStackLabels(),
        }, {
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.xAxisLabelsStyle,
          opposite: true,
        }],
        xAxis: [{
          categories: chartData.mainData.map((item) => {
            const compAmount = chartData.drilldownData[item.name]?.data.length;
            return `${item.name}<br>${compAmount?.toString()} comp.`;
          }),
          type: 'category',
          crosshair: true,
          style: { color: '#333333', fontSize: CHART_FONTS.font4 },
          labels: {
            rotation: -45,
            style: {
              width: 75,
              textOverflow: 'ellipsis',
              fontSize: CHART_FONTS.font4,
            },
          },
          scrollbar: CHART_STYLES.scrollbar,
        }],
        drilldown: {
          ...CHART_STYLES.drillDownOptions,
          series: FairValueAndPpChartsService.generateDrilldownSeries(chartData?.drilldownData, multiplicator),
        },
      },
    };
  }
}

const pieChartMap = {
  byFund: 'pie-chart-fair-value-by-fund',
  byCompany: 'pie-chart-fair-value-by-company',
  byIndustry: 'pie-chart-fair-value-by-industry',
  byCountry: 'pie-chart-fair-value-by-country',
  byGroup: 'pie-chart-fair-value-by-group',
  byAssets: 'pie-chart-fair-value-by-assets',
};

const barChartMap = {
  byFund: 'bar-chart-fair-value-by-fund',
  byIndustry: 'bar-chart-fair-value-by-industry',
  byCountry: 'bar-chart-fair-value-by-country',
  byGroup: 'bar-chart-fair-value-by-group',
  byAssets: 'bar-chart-fair-value-by-assets',
};
